import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useBilling, useMockSubscribe, startCheckout } from '../api/hooks'
import { fmtDate } from '../lib/format'
import { PageSpinner } from '../components/ui'
import { toast } from '../lib/toast'
import { errorMessage } from '../lib/errors'

export function Paywall() {
  const { t, locale } = useI18n()
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const { data: billing, isLoading } = useBilling()
  const subscribe = useMockSubscribe()

  // Back from Stripe Checkout (the webhook flips the plan asynchronously).
  useEffect(() => {
    const outcome = params.get('checkout')
    if (!outcome) return
    if (outcome === 'success') toast.success(t('billing.paid'))
    if (outcome === 'cancelled') toast.info(t('billing.cancelled'))
    params.delete('checkout')
    setParams(params, { replace: true })
  }, [params, setParams, t])

  // Card payment when Stripe is configured; the mock flow until then.
  const pay = async () => {
    try {
      const res = await startCheckout()
      if (res.url) {
        toast.info(t('billing.redirecting'))
        window.location.assign(res.url)
        return
      }
    } catch (e) {
      toast.error(errorMessage(e))
      return
    }
    subscribe.mutate(undefined, { onSuccess: () => nav('/app') })
  }

  if (isLoading || !billing) return <PageSpinner />
  const active = billing.plan === 'active'

  return (
    <main className="mx-auto max-w-lg px-5 py-12">
      <div className="card p-8 text-center">
        <div className="text-5xl">{active ? '✅' : billing.locked ? '🔒' : '⏳'}</div>
        <h1 className="mt-4 text-2xl font-extrabold tracking-tight">
          {active ? t('billing.activeTitle') : billing.locked ? t('billing.lockedTitle') : t('billing.trialTitle')}
        </h1>
        <p className="mt-2 text-brand-600 dark:text-brand-300">
          {active
            ? t('billing.activeBody')
            : billing.locked
              ? t('billing.lockedBody')
              : t('billing.trialBody', { days: billing.daysLeft, date: fmtDate(billing.trialEndsAt, locale) })}
        </p>

        {!active && (
          <>
            <div className="mt-6 rounded-2xl border border-brand-100 dark:border-brand-700/60 p-5 text-left">
              <div className="flex items-baseline justify-between">
                <span className="font-bold">HorseSheli</span>
                <span className="text-2xl font-extrabold">£4<span className="text-base font-medium text-brand-500">/{t('billing.month')}</span></span>
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-brand-600 dark:text-brand-300">
                <li>✓ {t('billing.feat1')}</li>
                <li>✓ {t('billing.feat2')}</li>
                <li>✓ {t('billing.feat3')}</li>
              </ul>
            </div>
            <button onClick={pay} disabled={subscribe.isPending} className="btn-primary mt-6 w-full disabled:opacity-60">
              {subscribe.isPending ? '…' : t('billing.subscribe')}
            </button>
            <p className="mt-3 text-xs text-brand-400">{t('billing.mockNote')}</p>
          </>
        )}
        {active && (
          <button onClick={() => nav('/app')} className="btn-primary mt-6">{t('billing.backToApp')}</button>
        )}
      </div>
    </main>
  )
}
